import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type CustodyEventView, type DeliveryStatus, type DeliveryView } from '@vello/api-client';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Drawer, EmptyState, Field, ListTable, Notice, PageHeader, Panel, Select, type Column, DrawerHeader, DrawerBody, DrawerFooter, SectionTitle, Cluster, Stack, Text, FieldCell, TimelineItem } from '@vello/web-theme';
import {
  SEND_WORKLIST_STATUSES,
  currentCustodyHolder,
  custodyTimeline,
  deliveryPinLocked,
  deliveryTone,
  formatMarketInstant,
  marketTimeZone,
  tailOf,
} from '../../src/index';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { SendInterventionPanel } from '../components/send-intervention-panel';
import { LoadingBlock } from '../components/loading-block';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';

/**
 * Send › Encomendas (W6-ADMIN-SCREENS, D401) — the operator worklist over `GET /v1/deliveries?status=&marketId=`,
 * a route with no console until now.
 *
 * `deliveries` is market-RLS'd, so the server **requires** a market (`MARKET_REQUIRED`, never an empty 200) and
 * answers one status at a time. The market comes from the scope selector or from the picker built out of the
 * operator's own grants — never a typed market id.
 *
 * A row opens the parcel with its **custody chain** (`GET /v1/deliveries/:id` answers the chain alongside the
 * snapshot): every recorded step in sequence order, who held the parcel at each one, and the proof reference when
 * there is one. A Send whose handover-PIN budget ran out (D389) is flagged there and on the row, because that is
 * what an operator looking for "why is this courier stuck" needs to see.
 */
export function SendDeliveriesScreen(): ReactElement {
  const { runtime, markets } = useAdmin();
  const { t, lang } = useT();
  const { v, fail } = useVocab();

  const { market, choices } = useScopeMarket();
  const timeZone = marketTimeZone(market, markets);

  const [status, setStatus] = useState<DeliveryStatus>('in_transit');
  const [rows, setRows] = useState<readonly DeliveryView[] | undefined>(undefined);
  const [listError, setListError] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<DeliveryView | undefined>(undefined);
  const [custody, setCustody] = useState<readonly CustodyEventView[] | undefined>(undefined);
  const readSeq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (market === undefined) {
      setRows(undefined);
      setListError(undefined);
      return;
    }
    const res = await runtime.delivery.listByStatus(status, market);
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setRows(res.value.deliveries);
      setListError(undefined);
    } else {
      setRows((prev) => prev ?? []);
      setListError(res.error.code === 'FORBIDDEN' ? t('w6.refused.forbidden') : t('w6.refused', { code: fail(res.error) }));
    }
  }, [runtime, market, status, t]);

  useEffect(() => {
    setRows(undefined);
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.live, enabled: market !== undefined });

  const openDetail = async (delivery: DeliveryView): Promise<void> => {
    setOpen(delivery);
    setCustody(undefined);
    const res = await runtime.delivery.get(delivery.deliveryId);
    if (res.ok) {
      setOpen(res.value.delivery);
      setCustody(res.value.custody);
    } else {
      setCustody([]);
    }
  };

  const columns: readonly Column<DeliveryView>[] = [
    { key: 'id', header: t('sdel.col.id'), width: 'minmax(120px, 1fr)', render: (d) => <IdRef value={d.deliveryId} /> },
    { key: 'status', header: t('sdel.col.status'), width: 'minmax(150px, 1fr)', render: (d) => <Badge tone={deliveryTone(d.status)}>{v('delivery.status', d.status)}</Badge> },
    {
      key: 'pin',
      header: t('sdel.col.pin'),
      width: 'minmax(140px, 1fr)',
      render: (d) =>
        deliveryPinLocked(d) ? (
          <Badge tone="danger">{t('sdel.pin.locked')}</Badge>
        ) : d.handoverPinFailures !== undefined && d.handoverPinFailures > 0 ? (
          <Text role="caption" tone="secondary">{t('sdel.pin.failures', { n: String(d.handoverPinFailures) })}</Text>
        ) : (
          '—'
        ),
    },
    { key: 'updated', header: t('sdel.col.updated'), width: 'minmax(150px, 1fr)', render: (d) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(d.updatedAt, timeZone, lang)}</Text> },
  ];

  const steps = custody === undefined ? undefined : custodyTimeline(custody);
  const holder = custody === undefined ? undefined : currentCustodyHolder(custody);

  return (
    <>
      <PageHeader
        title={t('sdel.title')}
        subtitle={t('sdel.subtitle')}
      />

      <Panel>
        <Stack gap={4}>
          <Cluster gap={4} wrap>
            <FieldCell size="sm">
              <Field label={t('sdel.field.status')} htmlFor="sdel-status">
                <Select id="sdel-status" value={status} onChange={(e) => setStatus(e.target.value as DeliveryStatus)}>
                  {SEND_WORKLIST_STATUSES.map((s) => (
                    <option key={s} value={s}>{v('delivery.status', s)}</option>
                  ))}
                </Select>
              </Field>
            </FieldCell>
          </Cluster>
        </Stack>
      </Panel>

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <ListTable
          columns={columns}
          rows={rows ?? []}
          rowKey={(d) => d.deliveryId}
          onRowClick={(d) => void openDetail(d)}
          selectedKey={open?.deliveryId}
          loading={rows === undefined}
          loadingLabel={t('ui.loading')}
          error={listError}
          onRetry={() => void load()}
          retryLabel={t('ui.retry')}
          empty={<EmptyState title={t('sdel.empty.title')} detail={t('sdel.empty.detail')} />}
          minWidth={860}
        />
      )}

      <Drawer open={open !== undefined} onClose={() => setOpen(undefined)} labelledBy="sdel-detail-title">
        {open !== undefined && (
          <>
            <DrawerHeader titleId="sdel-detail-title" title={t('sdel.detail.title', { id: tailOf(open.deliveryId) })} subtitle={<Badge tone={deliveryTone(open.status)}>{v('delivery.status', open.status)}</Badge>} />
            <DrawerBody>
              <DrawerStack>
                {deliveryPinLocked(open) && <Notice tone="danger">{t('sdel.pin.lockedDetail')}</Notice>}
                {holder !== undefined && (
                  <Text as="p" role="caption" tone="secondary">
                    {t('sdel.col.custody')}: <strong>{v('delivery.custody_holder', holder)}</strong>
                  </Text>
                )}
                {/* D473 (A-40): the operator's intervention on a stuck Send — confirm, status, proof, custody. */}
                <SendInterventionPanel delivery={open} onChanged={() => { void openDetail(open); void load(); }} />
                <section>
                  <SectionTitle level={3} size="sub">{t('sdel.custody.title')}</SectionTitle>
                  {steps === undefined ? (
                    <LoadingBlock lines={2} />
                  ) : steps.length === 0 ? (
                    <Text as="p" role="caption" tone="secondary">{t('sdel.custody.empty')}</Text>
                  ) : (
                    <Stack as="ol" gap={3}>
                      {steps.map((step) => (
                        <TimelineItem key={step.eventId}>
                          <Text role="label">
                            {t('sdel.custody.step', { kind: v('delivery.custody_event_kind', step.kind), from: v('delivery.custody_holder', step.from), to: v('delivery.custody_holder', step.to) })}
                          </Text>
                          <Text role="caption" tone="secondary" mono>{formatMarketInstant(step.at, timeZone, lang, 'datetime-seconds')}</Text>
                          {step.proofRef !== undefined && <Text role="caption" tone="secondary">{t('sdel.custody.proof', { ref: step.proofRef })}</Text>}
                        </TimelineItem>
                      ))}
                    </Stack>
                  )}
                </section>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setOpen(undefined)}>{t('ui.close')}</Button>
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
